import type { ReactNode } from "react";
import { FormikField } from "@components/forms/FormikField/FormikField";
import type {
    DateOnlyRange,
    DatePickerPreset,
} from "@design-system/DatePicker/DatePicker.types";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type { KitFieldProps } from "./kitFieldProps.types";

type DateRangeFieldValue = DateOnlyDateRange | null | undefined;

/** Paths of `Values` that hold a date-only range (`{ start?, end? }`). */
export type DateRangeFieldPath<Values> = FieldPath<
    Values,
    DateRangeFieldValue,
    DateOnlyDateRange
>;

export type DateRangeFieldProps<Values, Name extends string> = KitFieldProps<
    Name,
    HTMLButtonElement,
    DateOnlyDateRange | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> & {
        pinnedPresets?: readonly string[];
        onTogglePin?: (id: string) => void;
        /** Appended after the standard presets. */
        extraPresets?: ReadonlyArray<DatePickerPreset<DateOnlyRange>>;
        buttonClass?: string;
    };

/**
 * Month-range field with presets, bound to a typed Formik path. The "All
 * time" preset is the clear: it shows only when `clearable` comes with an
 * `emptyValue` the path allows, and writes that value.
 */
export const DateRangeField = <
    Values,
    Name extends DateRangeFieldPath<Values> = DateRangeFieldPath<Values>,
>(
    props: DateRangeFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        ref,
        disabled,
        required,
        onValueChange,
        pinnedPresets,
        onTogglePin,
        extraPresets,
        buttonClass,
    } = props;
    const clearTarget = toClearTarget(props);

    return (
        <FormikField<DateRangeFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => {
                const handleChange = (
                    range: DateOnlyDateRange,
                    isAllTime?: boolean,
                ) => {
                    if (!isAllTime) {
                        void helpers.setValue(range);
                        onValueChange?.(range);
                    } else if (clearTarget) {
                        void helpers.setValue(clearTarget.emptyValue);
                        onValueChange?.(clearTarget.emptyValue);
                    }
                };

                return (
                    <Field.Control
                        required={required}
                        render={(controlProps) => (
                            <DateRangeFilterNext
                                ref={ref}
                                name={name}
                                dateRange={field.value ?? {}}
                                onChange={handleChange}
                                onBlur={() => void helpers.setTouched(true)}
                                invalid={invalid}
                                disabled={disabled}
                                requireSelection={clearTarget === undefined}
                                pinnedPresets={pinnedPresets}
                                onTogglePin={onTogglePin}
                                extraPresets={extraPresets}
                                buttonClass={buttonClass}
                                fieldControlProps={{
                                    ...pickFieldControlAriaProps(controlProps),
                                    ...ariaLabelledbyProps(
                                        props["aria-labelledby"],
                                    ),
                                }}
                            />
                        )}
                    />
                );
            }}
        </FormikField>
    );
};
